"use client";

import type { RefObject } from "react";
import {
  CopyButton,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import type { KAPLAN_MEIER_CALCULATOR_STRINGS } from "@/components/tools/kaplan-meier-calculator";
import { ChartDownloads } from "@/components/tools/stats-clinical/chart";
import {
  DataGrid,
  WorkingLine,
} from "@/components/tools/stats-clinical/data-grid";
import { SurvivalPlot } from "@/components/tools/stats-clinical/km-plot";
import { t } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import {
  atRiskAt,
  type LogRankResult,
  type Observation,
  rowAt,
  type SurvivalCurve,
} from "@/lib/tool-kit/stats-clinical/kaplan-meier";
import { niceTicks } from "@/lib/tool-kit/stats-clinical/ticks";
import {
  apaBounded,
  type Formatter,
  fixed,
  formatP,
} from "@/lib/tool-kit/stats-research/format";

/*
 * Result panels of the Kaplan–Meier calculator. Strings are the widget's own.
 */

type Strings = typeof KAPLAN_MEIER_CALCULATOR_STRINGS;

const DASH = "—";
const MAX_ROWS = 60;

export interface KmData {
  names: string[];
  curves: SurvivalCurve[];
  groups: Observation[][];
  logRank: LogRankResult | null;
}

function time(fmt: Formatter, value: number | null, strings: Strings): string {
  return value === null
    ? strings.notReached
    : fmt(value, { maximumFractionDigits: 4 });
}

function prob(fmt: Formatter, value: number): string {
  return Number.isFinite(value) ? fixed(fmt, value, 3) : DASH;
}

function SummaryTable({
  data,
  strings,
  fmt,
}: {
  data: KmData;
  strings: Strings;
  fmt: Formatter;
}) {
  return (
    <DataGrid
      head={[
        strings.columnGroup,
        strings.columnN,
        strings.columnEvents,
        strings.columnCensored,
        strings.columnMedian,
        strings.columnMedianCi,
      ]}
      rows={data.curves.map((curve, i) => [
        data.names[i],
        fmt(curve.n),
        fmt(curve.events),
        fmt(curve.n - curve.events),
        time(fmt, curve.median.value, strings),
        `${time(fmt, curve.median.lower, strings)} – ${time(fmt, curve.median.upper, strings)}`,
      ])}
    />
  );
}

function LogRankPanel({
  data,
  strings,
  fmt,
}: {
  data: KmData;
  strings: Strings;
  fmt: Formatter;
}) {
  const result = data.logRank;
  if (!result) {
    return null;
  }
  const oneDecimal = (v: number) =>
    fmt(v, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (
    <section className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.logRankHeading}</h3>
      <p className="text-foreground text-sm">
        {t(strings.logRankText, {
          chi: fixed(fmt, result.chiSquare, 2),
          df: fmt(result.df),
          p: formatP(fmt, result.p),
        })}
      </p>
      <DataGrid
        head={[
          strings.columnGroup,
          strings.columnN,
          strings.columnObserved,
          strings.columnExpected,
          strings.columnOe,
        ]}
        rows={data.names.map((name, i) => [
          name,
          fmt(result.sizes[i]),
          fmt(result.observed[i]),
          oneDecimal(result.expected[i]),
          fixed(fmt, result.observed[i] / result.expected[i], 2),
        ])}
      />
      <p className="text-muted-foreground text-xs">{strings.logRankNote}</p>
    </section>
  );
}

function RiskTable({
  data,
  maxTime,
  strings,
  fmt,
}: {
  data: KmData;
  maxTime: number;
  strings: Strings;
  fmt: Formatter;
}) {
  const ticks = niceTicks(0, maxTime).values.filter((v) => v <= maxTime);
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.riskHeading}</h3>
      <DataGrid
        head={[strings.columnGroup, ...ticks.map((v) => fmt(v))]}
        rows={data.names.map((name, i) => [
          name,
          ...ticks.map((v) => fmt(atRiskAt(data.groups[i], v))),
        ])}
      />
    </section>
  );
}

function AtTimes({
  data,
  times,
  strings,
  fmt,
}: {
  data: KmData;
  times: number[];
  strings: Strings;
  fmt: Formatter;
}) {
  if (times.length === 0) {
    return null;
  }
  const rows = data.names.flatMap((name, i) =>
    times.map((value) => {
      const row = rowAt(data.curves[i], value);
      return [
        name,
        fmt(value),
        row ? fmt(row.atRisk) : DASH,
        row ? prob(fmt, row.survival) : strings.beyondFollowUp,
        row ? `${prob(fmt, row.lower)} – ${prob(fmt, row.upper)}` : DASH,
      ];
    })
  );
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.atTimesHeading}</h3>
      <DataGrid
        head={[
          strings.columnGroup,
          strings.columnTime,
          strings.columnAtRisk,
          strings.columnSurvival,
          strings.columnInterval,
        ]}
        rows={rows}
      />
    </section>
  );
}

function LifeTable({
  data,
  selected,
  onSelect,
  strings,
  fmt,
}: {
  data: KmData;
  selected: number;
  onSelect: (index: number) => void;
  strings: Strings;
  fmt: Formatter;
}) {
  const curve = data.curves[selected] ?? data.curves[0];
  const shown = curve.rows.slice(0, MAX_ROWS);
  const csv = () => {
    const head = [
      "group",
      "time",
      "at_risk",
      "events",
      "censored",
      "survival",
      "se",
      "lower",
      "upper",
    ];
    const lines = data.curves.flatMap((c, i) =>
      c.rows.map((r) =>
        [
          `"${data.names[i].replaceAll('"', '""')}"`,
          r.time,
          r.atRisk,
          r.events,
          r.censored,
          r.survival,
          r.se,
          r.lower,
          r.upper,
        ].join(",")
      )
    );
    downloadText(
      "kaplan-meier-table.csv",
      [head.join(","), ...lines].join("\n"),
      "text/csv;charset=utf-8"
    );
  };
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      {data.curves.length > 1 ? (
        <div className="max-w-xs">
          <SelectField
            id="km-table-group"
            label={strings.tableGroup}
            onChange={(value) => onSelect(Number(value))}
            options={data.names.map((name, i) => ({
              value: String(i),
              label: name,
            }))}
            value={String(selected)}
          />
        </div>
      ) : null}
      <div className="max-h-96 overflow-auto rounded-lg">
        <DataGrid
          head={[
            strings.columnTime,
            strings.columnAtRisk,
            strings.columnEvents,
            strings.columnCensored,
            strings.columnSurvival,
            strings.columnSe,
            strings.columnInterval,
          ]}
          rows={shown.map((r) => [
            fmt(r.time, { maximumFractionDigits: 4 }),
            fmt(r.atRisk),
            fmt(r.events),
            fmt(r.censored),
            prob(fmt, r.survival),
            prob(fmt, r.se),
            `${prob(fmt, r.lower)} – ${prob(fmt, r.upper)}`,
          ])}
        />
      </div>
      {curve.rows.length > shown.length ? (
        <p className="text-muted-foreground text-xs">
          {t(strings.tableMore, {
            shown: fmt(shown.length),
            total: fmt(curve.rows.length),
          })}
        </p>
      ) : null}
      <button className={BUTTON_SECONDARY} onClick={csv} type="button">
        {strings.downloadCsv}
      </button>
    </section>
  );
}

export function KmSummary({
  data,
  intervalName,
  showBands,
  showCensored,
  timeUnit,
  atTimes,
  selectedGroup,
  onSelectGroup,
  strings,
  fmt,
  svgRef,
}: {
  data: KmData;
  intervalName: string;
  showBands: boolean;
  showCensored: boolean;
  timeUnit: string;
  atTimes: number[];
  selectedGroup: number;
  onSelectGroup: (index: number) => void;
  strings: Strings;
  fmt: Formatter;
  svgRef: RefObject<SVGSVGElement | null>;
}) {
  const maxTime = Math.max(...data.curves.map((c) => c.maxTime), 1);
  const [first] = data.curves;
  const total = data.curves.reduce((sum, c) => sum + c.n, 0);
  const events = data.curves.reduce((sum, c) => sum + c.events, 0);
  const report = data.logRank
    ? t(strings.reportGroups, {
        chi: fixed(fmt, data.logRank.chiSquare, 2),
        df: fmt(data.logRank.df),
        p:
          data.logRank.p < 0.001
            ? `< ${apaBounded(fmt, 0.001, 3)}`
            : `= ${apaBounded(fmt, data.logRank.p, 3)}`,
        n: fmt(total),
      })
    : t(strings.reportSingle, {
        n: fmt(first.n),
        events: fmt(first.events),
        median: time(fmt, first.median.value, strings),
        lower: time(fmt, first.median.lower, strings),
        upper: time(fmt, first.median.upper, strings),
      });
  return (
    <div aria-live="polite" className="space-y-6">
      <h2 className={WIDGET_H2}>{strings.results}</h2>
      <SurvivalPlot
        curves={data.curves}
        formatNumber={(v) => fmt(v, { maximumFractionDigits: 2 })}
        label={t(strings.plotLabel, { groups: fmt(data.curves.length) })}
        names={data.names}
        showBands={showBands}
        showCensored={showCensored}
        survivalLabel={strings.yAxis}
        svgRef={svgRef}
        timeLabel={strings.xAxis}
        timeUnit={timeUnit}
      />
      <ChartDownloads
        baseName="kaplan-meier-curve"
        pngLabel={strings.downloadPng}
        svgLabel={strings.downloadSvg}
        svgRef={svgRef}
      />
      <StatGrid>
        <StatCard emphasis label={strings.cardSubjects} value={fmt(total)} />
        <StatCard label={strings.cardEvents} value={fmt(events)} />
        <StatCard
          label={
            data.curves.length > 1 && data.logRank
              ? strings.cardP
              : strings.cardMedian
          }
          value={
            data.curves.length > 1 && data.logRank
              ? formatP(fmt, data.logRank.p)
              : time(fmt, first.median.value, strings)
          }
        />
      </StatGrid>
      <SummaryTable data={data} fmt={fmt} strings={strings} />
      <LogRankPanel data={data} fmt={fmt} strings={strings} />
      <div className="flex flex-wrap items-center gap-3">
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyResult}
          text={report}
        />
        <span className="font-mono text-muted-foreground text-xs">
          {report}
        </span>
      </div>
      <RiskTable data={data} fmt={fmt} maxTime={maxTime} strings={strings} />
      <AtTimes data={data} fmt={fmt} strings={strings} times={atTimes} />
      <LifeTable
        data={data}
        fmt={fmt}
        onSelect={onSelectGroup}
        selected={selectedGroup}
        strings={strings}
      />
      <div className={`${CARD} space-y-2`}>
        <h3 className={WIDGET_H3}>{strings.howCalculated}</h3>
        <WorkingLine>{strings.workingKm}</WorkingLine>
        <WorkingLine>{strings.workingGreenwood}</WorkingLine>
        <p className="text-muted-foreground text-sm">
          {t(strings.workingNote, { interval: intervalName })}
        </p>
      </div>
    </div>
  );
}
